import { useAnalytics, useAnalyticsAttributes, useLanguage } from "..";
import { LANGUAGES, isLanguageCode } from "../../languages";
import { ColorId } from "../../styles";
import { setCookie } from "../../utils/storage";

import { LanguageForm } from "./LanguageSelectForm.styles";

export type LanguageSelectFormProps = {
    iconColor?: ColorId;
    selectLanguageLabel?: string;
    showGlobeIcon?: boolean;
};

export const LanguageSelectForm = ({
    iconColor = "N0",
    selectLanguageLabel,
    showGlobeIcon = true
}: LanguageSelectFormProps) => {
    const currentLanguage = useLanguage();
    const { sendTrackEvent } = useAnalytics();
    const analyticsAttributes = useAnalyticsAttributes();

    const ENGLISH_LANGUAGE_VARIANTS = ["en-AU", "en-US", "en-GB"];
    const languagesToDisplay = LANGUAGES.filter(
        ({ code }) => !ENGLISH_LANGUAGE_VARIANTS.includes(code)
    );

    return (
        <LanguageForm showGlobeIcon={showGlobeIcon} iconColor={iconColor}>
            <select
                aria-label={selectLanguageLabel}
                value={currentLanguage}
                onChange={({ target }) => {
                    const { value: selectedLanguage } = target;
                    const segments = location.pathname.split("/").slice(1);

                    sendTrackEvent("selectedLanguage", {
                        ...analyticsAttributes,
                        event: "selected",
                        eventComponent: "language",
                        label: `${selectedLanguage} language`,
                        labelEnglish: `${selectedLanguage} language`,
                        uuid: `${selectedLanguage}_language`
                    });

                    // Set language cookie for persistence (including "en")
                    // Cookie expires in 1 year
                    setCookie("lang", selectedLanguage, 365);

                    // Navigate to the new language URL with explicit locale prefix
                    if (isLanguageCode(segments[0]) && segments.length === 1) {
                        // Currently on /es or /fr, going to another language homepage
                        location.assign(`/${selectedLanguage}`);
                    } else if (isLanguageCode(segments[0])) {
                        // Currently on /es/enterprise, replace locale prefix
                        location.assign(
                            `/${selectedLanguage}/${segments
                                .slice(1)
                                .join("/")}`
                        );
                    } else if (location.pathname === "/") {
                        // Currently on homepage, add locale prefix
                        location.assign(`/${selectedLanguage}`);
                    } else {
                        // Currently on /enterprise (no locale), add locale prefix
                        location.assign(
                            `/${selectedLanguage}/${segments.join("/")}`
                        );
                    }
                }}
                data-testid="language-select"
            >
                {languagesToDisplay.map(({ code, name }) => (
                    <option
                        key={code}
                        value={code}
                        data-uuid={`${code}_language`}
                    >
                        {name}
                    </option>
                ))}
            </select>
        </LanguageForm>
    );
};
